import React, { useEffect } from 'react';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import { useMutation, useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Button,
  Paper,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Select,
  MenuItem,
  Alert,
  Skeleton,
  Chip,
} from '@mui/material';
import SwalReact from 'sweetalert2-react-content';
import Swal from 'sweetalert2';
import { updatePolicyVisibility } from 'services/transparency-config-service';
import { getPolicies } from 'services/policies-service';
import { DotsLoader } from 'Components/DotsLoader';

const MySwal = SwalReact(Swal);

/**
 * Policy Visibility Configuration
 * Configure which policies are visible and at what detail level
 */
export const PolicyVisibility = ({ config, onUpdate, companyKey }) => {
  const { t } = useTranslation(['transparency', 'common']);

  // Fetch all policies from policy service
  const { data: policiesData, isLoading: loadingPolicies, error: policiesError } = useQuery(
    ['policies', companyKey],
    () => getPolicies(companyKey),
    {
      enabled: !!companyKey,
      staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    }
  );

  // Initialize form
  const { control, handleSubmit, setValue, watch } = useForm({
    defaultValues: {
      policies: [],
    },
  });

  const { fields } = useFieldArray({
    control,
    name: 'policies',
  });

  // Enrich policies with saved config
  useEffect(() => {
    if (policiesData?.data && Array.isArray(policiesData.data)) {
      // Defensive: Ensure policyVisibility is an array
      const savedConfig = Array.isArray(config?.policyVisibility)
        ? config.policyVisibility
        : [];

      const enriched = policiesData.data.map((policy) => {
        const existing = savedConfig.find(s => s.policyId === policy._id);
        return {
          policyId: policy._id,
          policyName: policy.name,
          visibility: existing?.visibility || 'hidden',
          showExecutiveSummary: existing?.showExecutiveSummary ?? false,
          showFullContent: existing?.showFullContent ?? false,
        };
      });

      setValue('policies', enriched);
    }
  }, [policiesData, config, setValue]);

  // Save mutation
  const mutation = useMutation(
    (data) => updatePolicyVisibility(companyKey, data.policies),
    {
      onSuccess: () => {
        MySwal.fire({
          title: t('transparency:policyVisibility.notifications.success'),
          text: t('transparency:policyVisibility.notifications.successText'),
          icon: 'success',
        });
        onUpdate();
      },
      onError: (error) => {
        MySwal.fire({
          title: t('transparency:policyVisibility.notifications.error'),
          text: error.response?.data?.message || error.message,
          icon: 'error',
        });
      },
    }
  );

  const onSubmit = (data) => {
    mutation.mutate(data);
  };

  // Helper to determine if checkbox should be enabled based on visibility level
  const isExecutiveSummaryEnabled = (visibility) =>
    visibility === 'partial' || visibility === 'protected';

  const isFullContentEnabled = (visibility) =>
    visibility === 'protected';

  // Loading state
  if (loadingPolicies) {
    return (
      <Paper sx={{ p: 3 }}>
        <Skeleton variant="text" width={300} height={40} />
        <Skeleton variant="text" width="100%" />
        <Skeleton variant="rectangular" height={200} sx={{ mt: 2 }} />
      </Paper>
    );
  }

  // Error state
  if (policiesError || !companyKey) {
    return (
      <Paper sx={{ p: 3 }}>
        <Alert severity="error">
          {t('transparency:policyVisibility.error')}
        </Alert>
      </Paper>
    );
  }

  // Empty state
  if (!policiesData?.data || policiesData.data.length === 0) {
    return (
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          {t('transparency:policyVisibility.title')}
        </Typography>
        <Alert severity="info">
          {t('transparency:policyVisibility.empty')}
        </Alert>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 3 }}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Typography variant="h6" gutterBottom>
          {t('transparency:policyVisibility.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('transparency:policyVisibility.subtitle')}
        </Typography>

        {/* Legend */}
        <Box sx={{ mb: 3, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
          <Typography variant="subtitle2" gutterBottom>{t('transparency:policyVisibility.legend.title')}</Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Chip label={t('transparency:policyVisibility.levels.public')} color="success" size="small" />
            <Typography variant="caption">{t('transparency:policyVisibility.legend.publicDesc')}</Typography>
            <Chip label={t('transparency:policyVisibility.levels.partial')} color="warning" size="small" />
            <Typography variant="caption">{t('transparency:policyVisibility.legend.partialDesc')}</Typography>
            <Chip label={t('transparency:policyVisibility.levels.protected')} color="info" size="small" />
            <Typography variant="caption">{t('transparency:policyVisibility.legend.protectedDesc')}</Typography>
            <Chip label={t('transparency:policyVisibility.levels.hidden')} color="default" size="small" />
            <Typography variant="caption">{t('transparency:policyVisibility.legend.hiddenDesc')}</Typography>
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('transparency:policyVisibility.table.policy')}</TableCell>
                <TableCell align="center">{t('transparency:policyVisibility.table.visibility')}</TableCell>
                <TableCell align="center">
                  <Box>
                    <Typography variant="subtitle2">{t('transparency:policyVisibility.table.showSummary')}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t('transparency:policyVisibility.table.summarySub')}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell align="center">
                  <Box>
                    <Typography variant="subtitle2">{t('transparency:policyVisibility.table.showContent')}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t('transparency:policyVisibility.table.contentSub')}
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {fields.map((field, index) => {
                const currentVisibility = watch(`policies.${index}.visibility`);
                const summaryEnabled = isExecutiveSummaryEnabled(currentVisibility);
                const contentEnabled = isFullContentEnabled(currentVisibility);

                return (
                  <TableRow key={field.id}>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {field.policyName}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Controller
                        control={control}
                        name={`policies.${index}.visibility`}
                        render={({ field: { onChange, value } }) => (
                          <Select
                            value={value}
                            onChange={onChange}
                            size="small"
                            sx={{ minWidth: 120 }}
                          >
                            <MenuItem value="public">
                              <Chip label={t('transparency:policyVisibility.levels.public')} color="success" size="small" />
                            </MenuItem>
                            <MenuItem value="partial">
                              <Chip label={t('transparency:policyVisibility.levels.partial')} color="warning" size="small" />
                            </MenuItem>
                            <MenuItem value="protected">
                              <Chip label={t('transparency:policyVisibility.levels.protected')} color="info" size="small" />
                            </MenuItem>
                            <MenuItem value="hidden">
                              <Chip label={t('transparency:policyVisibility.levels.hidden')} color="default" size="small" />
                            </MenuItem>
                          </Select>
                        )}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Controller
                        control={control}
                        name={`policies.${index}.showExecutiveSummary`}
                        render={({ field: { onChange, value } }) => (
                          <Checkbox
                            checked={value}
                            onChange={onChange}
                            disabled={!summaryEnabled}
                            color="primary"
                          />
                        )}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Controller
                        control={control}
                        name={`policies.${index}.showFullContent`}
                        render={({ field: { onChange, value } }) => (
                          <Checkbox
                            checked={value}
                            onChange={onChange}
                            disabled={!contentEnabled}
                            color="primary"
                          />
                        )}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        <Divider sx={{ my: 2 }} />

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
          <Button
            type="submit"
            variant="contained"
            disabled={mutation.isLoading}
          >
            {mutation.isLoading ? <DotsLoader /> : t('transparency:policyVisibility.actions.save')}
          </Button>
        </Box>
      </form>
    </Paper>
  );
};
